import { GlButton, GlCollapsibleListbox, GlFormGroup, GlFormTextarea } from '/builds/gitlab/gitlab-ee/node_modules/@gitlab/ui/src/index.js?vue3';
import { cloneDeep, uniqueId } from 'lodash-es';
import { __, s__, sprintf } from '~/locale';
import { FLOW_TRIGGER_GOAL_MAX_LENGTH, FLOW_TRIGGER_MODE_SCHEDULE } from 'ee/ai/duo_agents_platform/constants';
import { eventConfigurationInvalidFeedback, getEventConfiguration, isEventConfigurationValid } from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/ai/duo_agents_platform/pages/flow_triggers/components/event_configurations/configurations.js?vue3';
export default {
    name: 'FlowTriggerConditionForm',
    components: {
        GlButton,
        GlCollapsibleListbox,
        GlFormGroup,
        GlFormTextarea
    },
    props: {
        // Whether this condition runs on a schedule or on an event, which decides whether the
        // event picker is shown. Distinct from `typeValue`, the specific event type.
        mode: {
            type: String,
            required: true
        },
        typeValue: {
            type: String,
            required: false,
            default: null
        },
        filter: {
            type: Object,
            required: false,
            default: ()=>({})
        },
        // Event types still selectable here: every enabled type minus the ones already added,
        // plus the one being edited. A type maps to a single filter scope, so it can appear once.
        typeOptions: {
            type: Array,
            required: true
        },
        isNew: {
            type: Boolean,
            required: false,
            default: false
        },
        serviceAccountRole: {
            type: String,
            required: false,
            default: null
        },
        goal: {
            type: String,
            required: false,
            default: ''
        },
        goalSupported: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'cancel',
        'save'
    ],
    data () {
        return {
            draftType: this.typeValue,
            draftFilter: cloneDeep(this.filter),
            draftGoal: this.goal,
            goalId: uniqueId('condition-goal-'),
            showErrors: false,
            headingId: uniqueId('condition-form-heading-')
        };
    },
    computed: {
        isSchedule () {
            return this.mode === FLOW_TRIGGER_MODE_SCHEDULE;
        },
        heading () {
            if (this.isSchedule) {
                return this.isNew ? s__('DuoAgentsPlatform|New schedule') : s__('DuoAgentsPlatform|Edit schedule');
            }
            return this.isNew ? s__('DuoAgentsPlatform|New event') : s__('DuoAgentsPlatform|Edit event');
        },
        submitText () {
            if (!this.isNew) {
                return __('Save');
            }
            return this.isSchedule ? s__('DuoAgentsPlatform|Add schedule') : s__('DuoAgentsPlatform|Add event');
        },
        selectedType () {
            return this.typeOptions.find((option)=>option.value === this.draftType);
        },
        typeToggleText () {
            return this.selectedType?.text ?? s__('DuoAgentsPlatform|Select an event');
        },
        configuration () {
            return this.draftType ? getEventConfiguration(this.draftType) : null;
        },
        // Only the schedule config surfaces the service account role, since scheduled runs
        // execute solely as the service account with no triggering user.
        configurationProps () {
            const props = {
                ...this.configuration?.props
            };
            if (this.isSchedule) {
                props.serviceAccountRole = this.serviceAccountRole;
            }
            return props;
        },
        isMissingType () {
            return !this.isSchedule && !this.draftType;
        },
        // An incomplete configuration is reported by the field that owns it, so the message is
        // tied to that control and marks it invalid. Only "no event picked yet" has no field of
        // its own to speak for it.
        configurationInvalidFeedback () {
            return this.showErrors && this.draftType ? eventConfigurationInvalidFeedback(this.draftType, this.draftFilter) : null;
        },
        missingTypeFeedback () {
            return this.showErrors && this.isMissingType ? s__('DuoAgentsPlatform|Select an event.') : null;
        },
        goalAvailable () {
            return this.goalSupported && !this.isSchedule;
        },
        isGoalTooLong () {
            return this.goalAvailable && this.draftGoal.trim().length > FLOW_TRIGGER_GOAL_MAX_LENGTH;
        },
        // No maxlength on the control: that truncates a long paste without telling anyone.
        goalInvalidFeedback () {
            return this.showErrors && this.isGoalTooLong ? sprintf(s__('DuoAgentsPlatform|Goal cannot exceed %{maxLength} characters.'), {
                maxLength: FLOW_TRIGGER_GOAL_MAX_LENGTH
            }) : null;
        }
    },
    methods: {
        onSelectType (typeValue) {
            // The configuration belongs to the event type. The goal is free text, so it stays.
            this.draftType = typeValue;
            this.draftFilter = {};
        },
        onUpdateFilter (nextFilter) {
            this.draftFilter = nextFilter;
        },
        onSubmit () {
            this.showErrors = true;
            if (this.isMissingType || this.isGoalTooLong || !isEventConfigurationValid(this.draftType, this.draftFilter)) {
                return;
            }
            this.$emit('save', {
                typeValue: this.draftType,
                filter: this.draftFilter,
                // draftGoal can still hold text typed before the field was hidden.
                goal: this.goalAvailable ? this.draftGoal.trim() : ''
            });
        }
    }
};
